'use client';

import { c as _c } from "react/compiler-runtime";
import SettingConfig from '@module/config/setting';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { createMessageHandlers, type MessageHandlerContext, USE_DEFAULT } from '@module/helpers/centrifuge/message-handlers';
import { createBlindOffUpdate, createBlindOnUpdate } from '@module/helpers/centrifuge/messageUpdates';
import useChatBatcher from '@module/helpers/chat/useChatBatcher';
import useAdultContentRedirection from '@module/helpers/play/useAdultContentRedirection';
import useAutoGoPlayOnDirectEntry from '@module/helpers/play/useAutoGoPlayOnDirectEntry';
import useAutoSpon from '@module/helpers/play/useAutoSpon';
import useCastResume from '@module/helpers/play/useCastResume';
import useEventRankState from '@module/helpers/play/useEventRankState';
import useForceAuthInterval from '@module/helpers/play/useForceAuthInterval';
import usePushYouthPolicyModal from '@module/helpers/play/usePushYouthPolicyModal';
import useRealTimePlayInformation from '@module/helpers/play/useRealTimePlayInformation';
import useSetSidePickListQueryData from '@module/helpers/play/useSetSidePickListQueryData';
import useHeartIconVIPOverlaySponEffect from '@module/helpers/useHeartIconVIPOverlaySponEffect';
import { plyrStateSafeLocalStorage } from '@module/lib/storage/storageImplementations';
import { cn } from '@module/lib/utils';
import { useFetchGetSitePresetQuery } from '@module/queries/preset/presetQueryHooks';
import useChatMessageStore from '@module/stores/useChatMessageStore';
import useChatPanelStore from '@module/stores/useChatPanelStore';
import { useFetchLivePlayMutationStore } from '@module/stores/useFetchLivePlayMutationStore';
import useModalStore from '@module/stores/useModalStore';
import useMuteStore from '@module/stores/useMuteStore';
import useOldPlayerStore from '@module/stores/useOldPlayerStore';
import usePlayInformationStore from '@module/stores/usePlayInformationStore';
import { isChatMessage } from '@module/types/centrifuge/chatTypes';
import { isServiceBlindOnMessage } from '@module/types/centrifuge/public-message/blindMessageTypes';
import { isSiteChatMessage } from '@module/types/centrifuge/public-message/normalChatMessageTypes';
import { isItemCoinMessage, isSponCoinMessage } from '@module/types/centrifuge/public-message/sponCoinMessageTypes';
import { MODAL_TYPE_PC } from '@module/types/modal/type';
import useCentrifuge from '@module/utils/useCentrifuge';
import PlayerInfomationSkeleton from '@pc/components/play/info/PlayerInfomationSkeleton';
import { Skeleton } from '@pc/components/ui/skeleton';
import PersonalMissionModalContent from '@pc/containers/modal/contents/PersonalMissionModalContent';
import useGoPlay from '@pc/hooks/play/useGoPlay';
import { useQueryClient } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useParams, useRouter } from 'next/navigation';
import { Suspense, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import PlayerSidePanel from './PlayerSidePanel';
function PlayerSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><div className="w-full px-5"><Skeleton className="relative h-full animate-pulse pb-[56.25%]" /></div><PlayerInfomationSkeleton /></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
const BroadcastListOnPlay = dynamic(() => import('./BroadcastListOnPlay'), {
  ssr: false
});
const PlayerArea = dynamic(() => import('@pc/components/play/video/PlayerArea'), {
  ssr: false,
  loading: PlayerSkeleton
});
function PlayContainer() {
  const $ = _c(31);
  const params = useParams();
  const userId = params?.userId ? decodeURIComponent(params.userId) : "";
  const queryClient = useQueryClient();
  const router = useRouter();
  const {
    userInfo,
    isLogin,
    isAdult,
    siteMode
  } = useGetLoginInfoQuery();
  const {
    pushModal,
    popModal,
    clearModals
  } = useModalStore();
  const setPlayInfo = useFetchLivePlayMutationStore(_temp);
  const {
    data: sitePreset
  } = useFetchGetSitePresetQuery();
  const {
    setVolumeBefore,
    setIsMute,
    setVolume,
    ivsPlayer,
    isPlayerFullscreen,
    dispose
  } = useOldPlayerStore(useShallow(_temp2));
  const setLike = usePlayInformationStore(_temp3);
  const setBookmark = usePlayInformationStore(_temp4);
  const triggers = useChatMessageStore(_temp5);
  const setChatMessages = useChatMessageStore(_temp6);
  const setMuteTime = useMuteStore(_temp7);
  const setMuteOnMessages = useMuteStore(_temp8);
  const isChatPanelOpen = useChatPanelStore(_temp9);
  const isGlobalChatVisible = useChatMessageStore(_temp10);
  const {
    goPlay,
    retryLivePlay
  } = useGoPlay();
  let t0;
  if ($[0] !== goPlay || $[1] !== userId) {
    t0 = {
      goPlay,
      userId
    };
    $[0] = goPlay;
    $[1] = userId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    playInfo
  } = useAutoGoPlayOnDirectEntry(t0);
  useAdultContentRedirection();
  usePushYouthPolicyModal();
  useForceAuthInterval();
  let t1;
  if ($[3] !== pushModal) {
    t1 = {
      onError: message => {
        pushModal({
          type: "CONFIRM_MODAL",
          id: "PlayContainer",
          options: {
            description: message
          }
        });
      }
    };
    $[3] = pushModal;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useAutoSpon(t1);
  useRealTimePlayInformation();
  useSetSidePickListQueryData();
  const {
    updateEventRank
  } = useEventRankState();
  const {
    overlayElem,
    enqueue: enqueueVIPSponEffect
  } = useHeartIconVIPOverlaySponEffect();
  const {
    startRetry
  } = useCastResume();
  const {
    processChatMessage
  } = useChatBatcher();
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = createMessageHandlers({
      onChatMessage: USE_DEFAULT,
      onSponMessage: USE_DEFAULT,
      onFreezeOn: USE_DEFAULT,
      onFreezeOff: USE_DEFAULT,
      onMuteOn: USE_DEFAULT,
      onMuteOff: USE_DEFAULT,
      onChatBlockCountry: USE_DEFAULT,
      onMission: USE_DEFAULT,
      onEventAnimCoin: USE_DEFAULT,
      onEventRank: USE_DEFAULT,
      onHeartIconVip: USE_DEFAULT,
      onVote: USE_DEFAULT,
      onModifyRoom: USE_DEFAULT,
      onSoundOn: _temp11,
      onSoundOff: _temp12,
      onMemberKickOut: USE_DEFAULT,
      onCastResume: USE_DEFAULT,
      onExcelBroadcast: USE_DEFAULT,
      onMediaUpdate: USE_DEFAULT,
      onBlindOn: _temp13,
      onBlindOff: _temp14,
      onRoomEnd: USE_DEFAULT,
      onFirstSpon: USE_DEFAULT,
      onPersonalAdmin: USE_DEFAULT,
      onPersonalSupport: USE_DEFAULT,
      onPersonalUpdateUserInfo: USE_DEFAULT,
      onPersonalUserAlert: USE_DEFAULT,
      onPersonalUserStop: USE_DEFAULT,
      onPersonalOverlapLogin: USE_DEFAULT,
      onPersonalManager: USE_DEFAULT,
      onPersonalMission: _temp15
    });
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    publicDispatcher,
    personalDispatcher
  } = t2;
  const getMessageContext = () => ({
    playInfo,
    userInfo,
    isLogin,
    isAdult,
    siteMode,
    sitePresetCode: sitePreset?.preset?.code,
    setPlayInfo,
    setChatMessages,
    setMuteTime,
    setMuteOnMessages,
    setLike,
    setBookmark,
    setVolume,
    setVolumeBefore,
    setIsMute,
    dispose,
    ivsPlayer,
    isGlobalChatVisible,
    processChatMessage,
    updateEventRank,
    enqueueVIPSponEffect,
    startRetry,
    pushModal,
    popModal,
    clearModals,
    router,
    queryClient,
    retryLivePlay,
    triggers: {
      eventAnimCoin: triggers.eventAnimCoin,
      introEffect: triggers.introEffect,
      castSettingRecommendEffect: triggers.castSettingRecommendEffect,
      playCastSettingKingFanInAudio: triggers.playCastSettingKingFanInAudio,
      playCastSettingFanInAudio: triggers.playCastSettingFanInAudio,
      firstSpon: triggers.firstSpon
    },
    getOutOfRoom
  });
  let t3;
  if ($[6] !== pushModal) {
    t3 = reason => {
      if (reason === "expired") {
        pushModal({
          type: "CONFIRM_MODAL",
          id: "PlayContainerCentrifugeDisconnect",
          options: {
            description: "\uD1A0\uD070\uC774 \uB9CC\uB8CC\uB418\uC5B4 \uCC44\uD305 \uC11C\uBC84 \uC5F0\uACB0\uC774 \uB04A\uC5B4\uC84C\uC2B5\uB2C8\uB2E4."
          }
        });
      }
    };
    $[6] = pushModal;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    disconnectSocket: t4
  } = useCentrifuge({
    onPublicMessage: payload_1 => {
      if (!playInfo) {
        return;
      }
      const isSponMessage = isSponCoinMessage(payload_1) || isItemCoinMessage(payload_1);
      const isAdultBlind = playInfo.playMode === "adult" && siteMode?.mode === "s";
      const isSiteUnmatched = isSiteChatMessage(payload_1) && payload_1.data.siteChat !== sitePreset?.preset?.code;
      const shouldChatHide = isChatMessage(payload_1) && (isAdultBlind && !isSponMessage || isSiteUnmatched);
      if (shouldChatHide) {
        return;
      }
      const context_4 = getMessageContext();
      context_4.disconnectSocket = disconnectSocket;
      publicDispatcher.dispatch(payload_1, context_4);
    },
    onPersonalMessage: payload_2 => {
      if (!playInfo) {
        return;
      }
      const context_5 = getMessageContext();
      context_5.disconnectSocket = disconnectSocket;
      personalDispatcher.dispatch(payload_2, context_5);
    },
    onDisconnect: t3,
    onMaxReconnectAttemptsReached: () => {
      pushModal({
        type: "CONFIRM_CANCEL_MODAL",
        id: "PlayContainer",
        options: {
          content: <div className="text-center"><p>채팅 서버 연결에 실패했습니다.</p><p>재시도 하시겠습니까?</p></div>,
          onClick: () => {
            retryLivePlay();
            popModal();
          },
          onCancel: () => {
            disconnectSocket();
            popModal();
          }
        }
      });
    }
  });
  const disconnectSocket = t4;
  const getOutOfRoom = msg => {
    dispose();
    disconnectSocket();
    pushModal({
      type: "CONFIRM_MODAL",
      id: "PlayContainer",
      options: {
        description: msg,
        onClose: () => {
          popModal();
          router.push("/");
        }
      }
    });
  };
  const isPlayInfoReady = playInfo && (playInfo.media.userId === userId || String(playInfo.media.userIdx) === userId);
  let t5;
  if ($[8] !== isPlayerFullscreen) {
    t5 = cn("flex h-[calc(100vh-64px)] w-full flex-col", {
      "mt-0 h-full": isPlayerFullscreen
    });
    $[8] = isPlayerFullscreen;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== isPlayerFullscreen) {
    t6 = cn("flex h-[calc(100vh-64px)] w-full", {
      "h-full": isPlayerFullscreen
    });
    $[10] = isPlayerFullscreen;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = !isChatPanelOpen;
  let t8;
  if ($[12] !== t7) {
    t8 = cn("w-[100%] min-w-[1032px] overflow-auto pr-[340px]", {
      "pr-0": t7
    });
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div id="player-portal" />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== isPlayInfoReady || $[16] !== playInfo || $[17] !== retryLivePlay) {
    t10 = !isPlayInfoReady ? <PlayerSkeleton /> : <Suspense fallback={<PlayerSkeleton />}><PlayerArea playInfo={playInfo} retryLivePlay={retryLivePlay} /></Suspense>;
    $[15] = isPlayInfoReady;
    $[16] = playInfo;
    $[17] = retryLivePlay;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Suspense fallback={null}><BroadcastListOnPlay /></Suspense>;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10 || $[21] !== t8) {
    t12 = <main id="player-area" data-testid="play-player" className={t8}>{t9}{t10}{t11}</main>;
    $[20] = t10;
    $[21] = t8;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <PlayerSidePanel />;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t12 || $[25] !== t6) {
    t14 = <section className={t6}>{t12}{t13}</section>;
    $[24] = t12;
    $[25] = t6;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== overlayElem || $[28] !== t14 || $[29] !== t5) {
    t15 = <div className={t5} data-testid="play-page">{overlayElem}{t14}</div>;
    $[27] = overlayElem;
    $[28] = t14;
    $[29] = t5;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}
function _temp15(payload_0, context_3) {
  context_3.pushModal?.({
    type: MODAL_TYPE_PC.CONFIRM_MODAL,
    id: "PlayContainer",
    options: {
      content: <PersonalMissionModalContent personalMissionPayload={payload_0} />
    }
  });
}
function _temp14(__1, context_2) {
  if (!context_2.playInfo) {
    return;
  }
  context_2.setIsMute?.(false);
  context_2.setPlayInfo(createBlindOffUpdate(context_2.playInfo));
}
function _temp13(payload, context_1) {
  if (!context_1.playInfo) {
    return;
  }
  if (isServiceBlindOnMessage(payload) && !SettingConfig.isFanza) {
    return;
  }
  context_1.setIsMute?.(true);
  context_1.setPlayInfo(createBlindOnUpdate(payload, context_1.playInfo));
}
function _temp12(__0, context_0) {
  context_0.setVolumeBefore?.(0.5);
  context_0.setIsMute?.(true);
  context_0.setVolume?.(0);
  context_0.ivsPlayer?.setMuted(true);
  context_0.ivsPlayer?.setVolume(0);
  plyrStateSafeLocalStorage.set({
    volume: 0,
    muted: true
  });
}
function _temp11(_, context) {
  context.setIsMute?.(false);
  context.setVolume?.(0.5);
  context.ivsPlayer?.setMuted(false);
  context.ivsPlayer?.setVolume(0.5);
  plyrStateSafeLocalStorage.set({
    volume: 0.5,
    muted: false
  });
}
function _temp10(state_7) {
  return state_7.isGlobalChatVisible;
}
function _temp9(state_6) {
  return state_6.isChatPanelOpen;
}
function _temp8(state_5) {
  return state_5.setMuteOnMessages;
}
function _temp7(state_4) {
  return state_4.setMuteTime;
}
function _temp6(state_3) {
  return state_3.setChatMessages;
}
function _temp5(state_2) {
  return state_2.triggers;
}
function _temp4(state_1) {
  return state_1.setBookmark;
}
function _temp3(state_0) {
  return state_0.setLike;
}
function _temp2(s) {
  return {
    setVolumeBefore: s.setVolumeBefore,
    setIsMute: s.setIsMute,
    setVolume: s.setVolume,
    ivsPlayer: s.ivsPlayer,
    isPlayerFullscreen: s.isPlayerFullscreen,
    dispose: s.dispose
  };
}
function _temp(state) {
  return state.setData;
}
export default PlayContainer;